// ==================================== COMPONENTS =========================================
// THIRD PARTY/ CORE COMPONENTS
import React, { useState } from 'react';
import propTypes from 'prop-types';
import { Button, Input, notification, Spin } from 'antd';
import { Link } from 'react-router-dom';
import { Modal } from '../../../components/modals/antd-modals';
import { BasicFormWrapper } from '../../styled';
import axios from '../../../redux/axios';
import FeatherIcon from 'feather-icons-react';
import { getHeaders } from '../../../utils/common';

const BulkUpload = ({ visible, onCancel, variant='add' }) => {
  const [file, setFile] = useState(null);
  const [uploadLoad, setUploadLoad] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [uploadRes, setUploadRes] = useState({ isUploaded: false, msg: '', errors: [] });

  const handleSubmit = async () => {
    setUploadLoad(true);
    try {
      const formData = new FormData();
      formData.append('excel', file);
      const apiUrl = variant === 'add' ? 'cars/bulk-create-via-excel' : 'cars/bulk-update-via-excel';
      const res = await axios.post(apiUrl, formData, {
        headers: { ...getHeaders() },
      });
      setUploadRes({ isUploaded: true, msg: res.data.msg || res.data.message, errors: res.data.errors });
      setUploadLoad(false);
    } catch (err) {
      if (err.response.status < 500) {
        setUploadRes({ isUploaded: true, msg: err.response.data.msg || err.response.data.message, errors: err.response.data.errors });
      } else {
        setUploadRes({ isUploaded: true, msg: 'Something is wrong. please check your file.', errors: [] });
      }
      setUploadLoad(false);
    }
  };

  const handleFileChange = (e) => {
    const vFile = e.target.files[0];

    if (vFile) {
      const allowedExtensions = ['csv', 'xls', 'xlsx'];
      const fileExtension = vFile.name.split('.').pop().toLowerCase();

      if (allowedExtensions.includes(fileExtension)) {
        setFile(vFile);
        setFileError(null); // Clear any previous error
      } else {
        setFile(null); // Clear any previously selected file
        setFileError('You can only upload CSV, XLS, or XLSX files!');
      }
    }
  };

  const handleDownloadTemplate = async() => {
    try {
      setDownloading(true);
      const templateApiUrl = variant === 'add' ? 'cars/bulk-create/template' : 'cars/bulk-update/template';
      const response = await axios.get(templateApiUrl, {
        headers: getHeaders(),
        responseType: 'blob',
      });

      if (response.status === 200) {
        // Ensure the request was successful
        const contentDisposition = response.headers['content-disposition'];
        const filenameMatch = contentDisposition ? contentDisposition.match(/filename="?(.+)"?/) : null;
        const fileName = filenameMatch ? filenameMatch[1].replace(/['"]/g, '') : 'Cars Bulk Update Sample.xlsx';
        // Create a blob from the response data
        const blob = new Blob([response.data], {
          type: response.headers['content-Type'],
        });
        const blobUrl = URL.createObjectURL(blob);

        // Create a link element and trigger the download
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.target = '_blank';
        link.click();

        // Clean up the URL object
        URL.revokeObjectURL(blobUrl);
        setDownloading(false);
      } else {
        setDownloading(false);
        notification.error({ message: response.data.message });
      }
    } catch (error) {
      notification.error({ message: 'Error generating the file' });
      setDownloading(false);
    }
  };

  const handleCancel = () => {
    setUploadRes({ isUploaded: false, msg: '', errors: [] });
    setFileError(null);
    setFile(null);
    onCancel();
  };

  const handleCancelAndReload = () => {
    handleCancel();
    window.location.reload();
  };

  return (
    <Modal
      type="primary"
      title={`Bulk ${variant === 'add' ? 'Add' : 'Update'} Cars`}
      visible={visible}
      footer={[
        <div key="1" className="blog-modal-footer">
          <Button
            disabled={!!fileError || !file}
            size="default"
            style={{ backgroundColor: '#155BDC', color: '#fff' }}
            key="submit"
            onClick={uploadRes.isUploaded ? handleCancelAndReload : handleSubmit}
          >
            {uploadRes.isUploaded ? 'Ok' : 'Submit'}
          </Button>
          <Button size="default" type="white" key="back" outlined onClick={onCancel}>
            Cancel
          </Button>
        </div>,
      ]}
      onCancel={onCancel}
    >
      <div className="blog-modal">
        {uploadLoad ? (
          <div style={{ textAlign: 'center' }}>
            <Spin size="44" />
          </div>
        ) : uploadRes?.isUploaded ? (
          <>
            <div>
              <h3>{uploadRes?.msg}</h3>
              <ul>
                {uploadRes?.errors?.map((error, index) => (
                  <li key={index}>
                    Row {error?.row}: {error?.error || error?.message}
                  </li>
                ))}
              </ul>
            </div>
          </>
        ) : (
          <BasicFormWrapper>
            <Input
              type="file"
              required
              name="images"
              onChange={handleFileChange}
              accept=".xls,.xlsx,.csv"
              style={{ cursor: 'pointer' }}
            />
            <p style={{ color: 'red' }}>{fileError}</p>
            <div style={{ marginTop: '1rem' }}>
              {
                variant === 'add' ? (
                  <Link
                    to="#"
                    style={{ display: 'flex', alignItems: 'center' }}
                    onClick={() => {
                      handleDownloadTemplate();
                    }}
                  >
                    {
                      downloading ? (
                        <Spin size="small" style={{ marginRight: '4px' }} />
                      ) : (
                        <FeatherIcon icon="download-cloud" size={14} style={{ marginRight: '4px' }} />
                      )
                    }
                    <span style={{ lineHeight: '142%' }}> Download the sample file for bulk upload.</span>
                  </Link>
                ) : (
                  <Link
                    to="#"
                    style={{ display: 'flex', alignItems: 'center' }}
                    onClick={() => {
                      handleDownloadTemplate();
                    }}
                  >
                    {
                      downloading ? (
                        <Spin size="small" style={{ marginRight: '4px' }} />
                      ) : (
                        <FeatherIcon icon="download-cloud" size={14} style={{ marginRight: '4px' }} />
                      )
                    }
                    <span style={{ lineHeight: '142%' }}> Download the sample file for bulk update.</span>
                  </Link>
                )
              }
            </div>
          </BasicFormWrapper>
        )}
      </div>
    </Modal>
  );
};

BulkUpload.propTypes = {
  visible: propTypes.bool.isRequired,
  onCancel: propTypes.func.isRequired,
};

export default BulkUpload;
